import { FormattedMessage } from 'react-intl';
import { InformationOutlinedIcon, ShoppingCartIcon, SingleInquiryIcon } from '@synergy/theme-next';
import { PoolButton, PoolQuantityPicker, PoolRow, PoolTypography, useIsMobile } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement } from 'react';
import { Stack, Tooltip, styled } from '@mui/material';
import { UserRole } from '@synergy/models';

import { AccessControl } from '../../../components/access-control/access-control';
import { DeliveryStatus } from '../../../components/delivery-status/delivery-status';
import { FavoriteButton } from '../../../components/favorite-button/favorite-button';
import { VerticalDivider } from '../../../components/vertical-divider/vertical-divider';
import { useAppSelector } from '../../../../store/store';
import { useArticleDetailContext } from '../article-detail';
import { userSelector } from '../../../store/configuration-selectors';

const SubmitButton = styled(PoolButton)(() => ({
    minWidth: 200,
    flex: 1,
}));

export const ArticleDetailDeliveryActions = (): ReactElement | null => {
    const { article, orderQuantity, onToggleFavorite, onAddToShoppingCart, onSingleInquiry, onChangeOrderQuantity } =
        useArticleDetailContext();
    const user = useAppSelector(userSelector);
    const { orderUnit, inventory, isAvailable } = article;
    const isMobile = useIsMobile();

    const handleFavoriteClick = (): void => {
        onToggleFavorite(article.id, orderQuantity);
    };

    const handleShoppingCartClick = (): void => {
        onAddToShoppingCart(article.id, orderQuantity);
    };

    const handleSingleInquiryClick = (): void => {
        onSingleInquiry(orderQuantity);
    };

    return (
        <>
            <PoolRow gap={1.5} color="grey.800" typography="body2">
                <DeliveryStatus
                    deliveryStatus={article.deliveryStatus}
                    stockStatus={article.stockStatus}
                    deliveryTimeInDays={article.deliveryTimeInDays}
                />
                {inventory && (
                    <Stack direction="row">
                        ({inventory} {orderUnit})
                    </Stack>
                )}
            </PoolRow>

            {(article.minimumOrderQuantity || article.recommendedOrderQuantity) && (
                <Stack direction={isMobile ? 'column' : 'row'} gap={isMobile ? 0.5 : 1.5} alignItems="baseline">
                    <PoolTypography
                        variant="statusLabel"
                        translationId={
                            orderUnit ? 'app.article.detail.orderQuantityWithUnit' : 'app.article.detail.orderQuantity'
                        }
                        values={orderUnit ? { unit: orderUnit } : undefined}
                    />
                    <Stack direction="row" typography="body2" color="grey.800" gap={1} divider={<VerticalDivider />}>
                        {article.minimumOrderQuantity && (
                            <FormattedMessage
                                id="app.article.detail.minimumOrderQuantity"
                                values={{
                                    quantity: article.minimumOrderQuantity,
                                }}
                            />
                        )}
                        {article.quantityInterval && article.quantityInterval !== 1 && (
                            <FormattedMessage
                                id="app.article.detail.interval"
                                values={{
                                    interval: article.quantityInterval,
                                }}
                            />
                        )}
                        {article.recommendedOrderQuantity && (
                            <FormattedMessage
                                id="app.article.detail.recommendedOrderQuantity"
                                values={{
                                    quantity: article.recommendedOrderQuantity,
                                }}
                            />
                        )}
                    </Stack>
                </Stack>
            )}

            <AccessControl user={user} allowedRoles={[UserRole.ShopAdmin, UserRole.ShopUser]}>
                <Stack direction="row" columnGap={2} rowGap={1.5} width="100%" flexWrap="wrap">
                    <PoolQuantityPicker
                        quantity={orderQuantity}
                        minQuantity={article.minimumOrderQuantity}
                        maxQuantity={article.maximumOrderQuantity}
                        interval={article.quantityInterval}
                        sx={isMobile ? { flex: '1 1 0' } : {}}
                        onQuantityChange={onChangeOrderQuantity}
                    />
                    <FavoriteButton isFavorite={article.isFavorite} onClick={handleFavoriteClick} />
                    {isAvailable ? (
                        <SubmitButton startIcon={<ShoppingCartIcon />} onClick={handleShoppingCartClick}>
                            <FormattedMessage id="app.article.detail.intoShoppingCart" />
                        </SubmitButton>
                    ) : (
                        <PoolRow flex={1}>
                            <SubmitButton startIcon={<SingleInquiryIcon />} onClick={handleSingleInquiryClick}>
                                <FormattedMessage id="app.singleInquiry" />
                            </SubmitButton>
                            <Tooltip
                                variant="light"
                                placement="top-end"
                                title={
                                    <Stack gap={0.5}>
                                        <PoolRow>
                                            <InformationOutlinedIcon />
                                            <strong>
                                                <FormattedMessage id="app.singleInquiry.tooltip" />
                                            </strong>
                                        </PoolRow>
                                        <PoolFormattedMessage id="app.singleInquiry.info" />
                                    </Stack>
                                }
                            >
                                <Stack typography="h4">
                                    <InformationOutlinedIcon color="primary" fontSize="inherit" />
                                </Stack>
                            </Tooltip>
                        </PoolRow>
                    )}
                </Stack>
            </AccessControl>
        </>
    );
};
